import { queryOptions, useQuery } from '@tanstack/react-query';

import { PodMutation } from '@cast/types-patching-engine';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const POD_MUTATION_QUERY_KEY = 'autoscaler-pod-mutation';

export const getPodMutationQueryKey = (
  clusterId?: string,
  mutationId?: string
) => {
  if (clusterId === undefined) return [POD_MUTATION_QUERY_KEY] as const;
  if (mutationId === undefined)
    return [POD_MUTATION_QUERY_KEY, clusterId] as const;
  return [POD_MUTATION_QUERY_KEY, clusterId, mutationId] as const;
};

const getPodMutationQueryOptions = ({
  organizationId,
  clusterId,
  mutationId,
}: {
  organizationId?: string;
  clusterId?: string;
  mutationId?: string;
}) =>
  queryOptions({
    queryKey: getPodMutationQueryKey(clusterId, mutationId),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getPodMutation({
        organizationId: organizationId!,
        clusterId: clusterId!,
        id: mutationId!,
      });
      return data;
    },
    enabled: !!mutationId && !!clusterId && !!organizationId,
  });

type UsePodMutationQuery = {
  isLoading: boolean;
  podMutation?: PodMutation;
  error: unknown;
  refetch?: () => void;
};

export const usePodMutationQuery = (
  clusterId?: string,
  mutationId?: string
): UsePodMutationQuery => {
  const { currentOrganization } = useOrganizations();

  const { isLoading, data, error, refetch } = useQuery(
    getPodMutationQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      mutationId,
    })
  );

  return {
    isLoading,
    podMutation: data,
    error,
    refetch,
  };
};
